import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { API } from "../lib/api";

const TIERS = [
    { key: "voucher_25", amount: 25 },
    { key: "voucher_50", amount: 50 },
    { key: "voucher_75", amount: 75 },
    { key: "voucher_100", amount: 100 },
];

// Kill-switch — flip to `false` once Stripe live keys are pushed to prod and
// checkout is verified. When true, the form still renders (so customers can
// see what's coming) but the pay button is disabled and shows a friendly
// "Vouchers coming soon" state.
const VOUCHERS_COMING_SOON = false;

export default function VouchersPage() {
    const [tier, setTier] = useState(null);
    const [buyer, setBuyer] = useState({ name: "", email: "", mobile: "" });
    const [isGift, setIsGift] = useState(false);
    const [recipient, setRecipient] = useState({
        name: "",
        email: "",
        phone: "",
    });
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    async function onSubmit(e) {
        e.preventDefault();
        if (VOUCHERS_COMING_SOON) return;
        setError("");
        // Client-side gift validation: name required + at least one contact
        if (isGift) {
            if (!recipient.name.trim()) {
                setError("Please enter the recipient's name.");
                return;
            }
            if (!recipient.email.trim() && !recipient.phone.trim()) {
                setError(
                    "Enter the recipient's email or mobile — one is needed so we can deliver the voucher."
                );
                return;
            }
        }
        setLoading(true);
        try {
            const res = await fetch(`${API}/api/vouchers/checkout`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    lookup_key: tier,
                    buyer_name: buyer.name,
                    buyer_email: buyer.email,
                    buyer_mobile: buyer.mobile,
                    is_gift: isGift,
                    recipient_name: isGift ? recipient.name : undefined,
                    recipient_email:
                        isGift && recipient.email
                            ? recipient.email
                            : undefined,
                    recipient_phone:
                        isGift && recipient.phone
                            ? recipient.phone
                            : undefined,
                    message: isGift ? message : undefined,
                    origin_url: window.location.origin,
                }),
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.detail || "Failed to start checkout");
            }
            const { checkout_url } = await res.json();
            window.location.href = checkout_url;
        } catch (err) {
            setError(err.message || "Something went wrong");
            setLoading(false);
        }
    }

    const selectedAmount = TIERS.find((t) => t.key === tier)?.amount || 0;
    const canSubmit = !!tier && !loading;

    return (
        <section
            data-testid="vouchers-page"
            className="min-h-[80vh] bg-[#FAF8F4] pt-32 md:pt-40 pb-24"
        >
            <div className="mx-auto max-w-[1100px] px-6 md:px-10 lg:px-14">
                {VOUCHERS_COMING_SOON && (
                    <div
                        data-testid="vouchers-coming-soon-banner"
                        className="mb-8 md:mb-10 p-4 md:p-5 bg-[#121212] text-[#FAF8F4] flex items-center gap-3"
                    >
                        <span
                            aria-hidden="true"
                            className="text-[#EB4A18] font-display text-2xl leading-none"
                        >
                            •
                        </span>
                        <p className="text-sm md:text-base leading-relaxed">
                            <span className="overline !text-[#EB4A18] mr-2">
                                Launching soon
                            </span>
                            Gift vouchers go live in the next day or two — we're
                            finalising our payment setup. Check back very
                            shortly.
                        </p>
                    </div>
                )}
                {/* Heading + right-side gift box animation */}
                <div className="flex items-center justify-between gap-4 md:gap-10">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, ease: [0.19, 1, 0.22, 1] }}
                        className="flex-1 min-w-0"
                    >
                        <p className="overline mb-3">Gift Vouchers</p>
                        <h1 className="font-display uppercase text-[clamp(2.5rem,7vw,5.5rem)] leading-[0.92] text-[#121212]">
                            Give a
                            <br />
                            <span className="text-[#EB4A18]">Gaffer feed.</span>
                        </h1>
                        <p className="mt-6 max-w-xl text-base md:text-lg text-[#3A3A3A] leading-relaxed">
                            Fully digital, delivered by email in seconds. Use at
                            the counter — valid for 3 years, no cash back, unused
                            balance stays on the voucher.
                        </p>
                    </motion.div>
                    <div className="shrink-0" aria-hidden="true">
                        <BuildingGiftBox />
                    </div>
                </div>

                {/* Amount tiles */}
                <div className="mt-14">
                    <p className="overline mb-4">Choose an amount</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                        {TIERS.map((t) => {
                            const active = t.key === tier;
                            return (
                                <button
                                    key={t.key}
                                    type="button"
                                    data-testid={`voucher-tier-${t.amount}`}
                                    onClick={() => setTier(t.key)}
                                    className={`relative p-6 md:p-8 border-2 transition-all duration-200 ${
                                        active
                                            ? "border-[#EB4A18] bg-[#EB4A18] text-[#FAF8F4]"
                                            : "border-[#121212]/15 bg-white text-[#121212] hover:border-[#121212]/50"
                                    }`}
                                >
                                    <p className="font-display text-[clamp(2rem,5vw,3rem)] leading-none">
                                        ${t.amount}
                                    </p>
                                    <p
                                        className={`mt-2 text-[0.65rem] uppercase tracking-[0.22em] font-bold ${
                                            active
                                                ? "text-[#FAF8F4]/80"
                                                : "text-[#6B6B6B]"
                                        }`}
                                    >
                                        AUD Voucher
                                    </p>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Form */}
                <form
                    onSubmit={onSubmit}
                    className="mt-14 grid md:grid-cols-2 gap-x-8 gap-y-6"
                >
                    {/* Buyer */}
                    <div className="md:col-span-2">
                        <p className="overline mb-4">Your details</p>
                    </div>
                    <FormField
                        label="Your name"
                        testId="buyer-name"
                        value={buyer.name}
                        onChange={(v) => setBuyer({ ...buyer, name: v })}
                        required
                    />
                    <FormField
                        label="Your email"
                        type="email"
                        testId="buyer-email"
                        value={buyer.email}
                        onChange={(v) => setBuyer({ ...buyer, email: v })}
                        required
                    />
                    <FormField
                        label="Your mobile"
                        type="tel"
                        testId="buyer-mobile"
                        value={buyer.mobile}
                        onChange={(v) => setBuyer({ ...buyer, mobile: v })}
                        required
                        className="md:col-span-2"
                    />

                    {/* Gift toggle */}
                    <div className="md:col-span-2 mt-4">
                        <label className="flex items-center gap-3 cursor-pointer select-none">
                            <input
                                type="checkbox"
                                data-testid="is-gift-toggle"
                                checked={isGift}
                                onChange={(e) => setIsGift(e.target.checked)}
                                className="w-5 h-5 accent-[#EB4A18]"
                            />
                            <span className="text-sm uppercase tracking-[0.18em] font-bold text-[#121212]">
                                This is a gift — send it to someone else
                            </span>
                        </label>
                    </div>

                    {isGift && (
                        <>
                            <div className="md:col-span-2 mt-4">
                                <p className="overline mb-1">
                                    Recipient details
                                </p>
                                <p className="text-[0.65rem] uppercase tracking-[0.18em] text-[#6B6B6B]">
                                    We&apos;ll send the voucher to their email
                                    or SMS — enter at least one
                                </p>
                            </div>
                            <FormField
                                label="Their name"
                                testId="recipient-name"
                                value={recipient.name}
                                onChange={(v) =>
                                    setRecipient({ ...recipient, name: v })
                                }
                                required
                                className="md:col-span-2"
                            />
                            <FormField
                                label="Their email"
                                type="email"
                                testId="recipient-email"
                                value={recipient.email}
                                onChange={(v) =>
                                    setRecipient({ ...recipient, email: v })
                                }
                            />
                            <FormField
                                label="Their mobile"
                                type="tel"
                                testId="recipient-phone"
                                value={recipient.phone}
                                onChange={(v) =>
                                    setRecipient({ ...recipient, phone: v })
                                }
                            />
                            <div className="md:col-span-2">
                                <label className="block text-[0.65rem] uppercase tracking-[0.22em] font-bold text-[#6B6B6B] mb-2">
                                    Message (optional)
                                </label>
                                <textarea
                                    data-testid="gift-message"
                                    value={message}
                                    maxLength={300}
                                    onChange={(e) => setMessage(e.target.value)}
                                    placeholder="Happy birthday, hope you love The Gaffer as much as I do..."
                                    rows={3}
                                    className="w-full p-4 border-2 border-[#121212]/15 focus:border-[#EB4A18] outline-none bg-white text-[#121212] text-sm resize-none"
                                />
                                <p className="text-[0.6rem] uppercase tracking-[0.22em] text-[#6B6B6B] mt-1">
                                    {message.length}/300
                                </p>
                            </div>
                        </>
                    )}

                    {/* Error */}
                    {error && (
                        <p
                            data-testid="vouchers-error"
                            className="md:col-span-2 text-sm text-[#EB4A18]"
                        >
                            {error}
                        </p>
                    )}

                    {/* Submit */}
                    <div className="md:col-span-2 mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-t border-black/10 pt-8">
                        <div>
                            <p className="overline">Total</p>
                            {tier ? (
                                <p className="font-display text-4xl md:text-5xl text-[#121212] leading-none mt-1">
                                    ${selectedAmount}
                                    <span className="text-xl text-[#6B6B6B] ml-2">
                                        AUD
                                    </span>
                                </p>
                            ) : (
                                <p className="font-display text-4xl md:text-5xl text-[#121212]/25 leading-none mt-1">
                                    —
                                </p>
                            )}
                        </div>
                        <button
                            type="submit"
                            disabled={VOUCHERS_COMING_SOON || !canSubmit}
                            data-testid="vouchers-checkout-btn"
                            className="btn-primary text-sm md:text-base px-8 md:px-10 py-4 md:py-5 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            {VOUCHERS_COMING_SOON
                                ? "Vouchers coming soon"
                                : loading
                                ? "Redirecting…"
                                : tier
                                ? "Pay securely with Stripe"
                                : "Choose an amount above"}
                            <span aria-hidden="true">→</span>
                        </button>
                    </div>

                    <p className="md:col-span-2 text-[0.6rem] uppercase tracking-[0.22em] text-[#6B6B6B] text-center">
                        Powered by Stripe · Valid for 3 years · No cash back
                    </p>
                </form>

                {/* "Already have a voucher?" — small orange link */}
                <div className="mt-16 pt-8 border-t border-black/10 flex justify-center">
                    <Link
                        to="/vouchers/balance"
                        data-testid="vouchers-check-balance-link"
                        className="text-sm md:text-base uppercase tracking-[0.2em] font-bold text-[#EB4A18] hover:text-[#121212] transition-colors inline-flex items-center gap-2 border-b-2 border-[#EB4A18] pb-1.5"
                    >
                        Already have a voucher? Check balance
                        <span aria-hidden="true">→</span>
                    </Link>
                </div>
            </div>
        </section>
    );
}

function FormField({
    label,
    value,
    onChange,
    type = "text",
    testId,
    required,
    className = "",
}) {
    return (
        <div className={className}>
            <label className="block text-[0.65rem] uppercase tracking-[0.22em] font-bold text-[#6B6B6B] mb-2">
                {label}
                {required && <span className="text-[#EB4A18] ml-1">*</span>}
            </label>
            <input
                type={type}
                data-testid={testId}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                required={required}
                className="w-full p-4 border-2 border-[#121212]/15 focus:border-[#EB4A18] outline-none bg-white text-[#121212] text-sm"
            />
        </div>
    );
}


// ---------------------------------------------------------------------------
// Desktop-only gift box that builds layer-by-layer on mount. Same hand-drawn
// SVG aesthetic as the burger animation on the Menu / HowToOrder section.
// Sequence: box shadow → box body → lid → vertical ribbon → horizontal ribbon
// → bow loops → bow knot → sparkles.
// ---------------------------------------------------------------------------
function GiftLayer({ delay, drop = 24, children }) {
    return (
        <motion.g
            initial={{ opacity: 0, y: -drop }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
                duration: 0.55,
                delay,
                ease: [0.19, 1, 0.22, 1],
            }}
        >
            {children}
        </motion.g>
    );
}

function BuildingGiftBox() {
    return (
        <svg
            viewBox="0 0 200 200"
            className="w-[110px] sm:w-[150px] md:w-[220px] lg:w-[260px] h-auto overflow-visible"
        >
            {/* Ground shadow */}
            <motion.ellipse
                cx="100"
                cy="188"
                rx="82"
                ry="6"
                fill="rgba(0,0,0,0.14)"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4 }}
            />

            {/* 1. Box body (dark, arrives first) */}
            <GiftLayer delay={0.05}>
                <rect
                    x="30"
                    y="86"
                    width="140"
                    height="98"
                    fill="#121212"
                />
                {/* Left face shading */}
                <rect
                    x="30"
                    y="86"
                    width="8"
                    height="98"
                    fill="#000"
                    opacity="0.35"
                />
                {/* Bottom highlight */}
                <rect
                    x="30"
                    y="180"
                    width="140"
                    height="4"
                    fill="#3A3A3A"
                    opacity="0.6"
                />
            </GiftLayer>

            {/* 2. Box lid (drops onto the body) */}
            <GiftLayer delay={0.22} drop={40}>
                <rect
                    x="22"
                    y="74"
                    width="156"
                    height="20"
                    fill="#1A1A1A"
                />
                {/* Lid highlight strip */}
                <rect
                    x="22"
                    y="74"
                    width="156"
                    height="3"
                    fill="#3A3A3A"
                    opacity="0.7"
                />
                {/* Left corner shadow */}
                <rect
                    x="22"
                    y="74"
                    width="8"
                    height="20"
                    fill="#000"
                    opacity="0.4"
                />
            </GiftLayer>

            {/* 3. Vertical ribbon (down the front of the box) */}
            <GiftLayer delay={0.4}>
                <rect
                    x="88"
                    y="74"
                    width="24"
                    height="110"
                    fill="#EB4A18"
                />
                {/* Ribbon inner shading */}
                <rect
                    x="88"
                    y="74"
                    width="4"
                    height="110"
                    fill="#B83610"
                    opacity="0.6"
                />
                {/* Ribbon highlight */}
                <rect
                    x="106"
                    y="74"
                    width="2"
                    height="110"
                    fill="#F87C4A"
                    opacity="0.55"
                />
            </GiftLayer>

            {/* 4. Horizontal ribbon (across the lid) */}
            <GiftLayer delay={0.55}>
                <rect
                    x="22"
                    y="80"
                    width="156"
                    height="10"
                    fill="#EB4A18"
                />
                <rect
                    x="22"
                    y="80"
                    width="156"
                    height="2"
                    fill="#F87C4A"
                    opacity="0.55"
                />
                <rect
                    x="22"
                    y="88"
                    width="156"
                    height="2"
                    fill="#B83610"
                    opacity="0.6"
                />
            </GiftLayer>

            {/* 5. Bow — left loop */}
            <GiftLayer delay={0.72} drop={16}>
                <path
                    d="M100 62 Q68 40 58 56 Q54 66 68 72 Q84 76 100 68 Z"
                    fill="#EB4A18"
                />
                <path
                    d="M100 62 Q78 48 66 58 Q62 64 72 68 Q86 70 100 66"
                    fill="none"
                    stroke="#B83610"
                    strokeWidth="1.2"
                    opacity="0.55"
                />
            </GiftLayer>

            {/* 6. Bow — right loop */}
            <GiftLayer delay={0.82} drop={16}>
                <path
                    d="M100 62 Q132 40 142 56 Q146 66 132 72 Q116 76 100 68 Z"
                    fill="#EB4A18"
                />
                <path
                    d="M100 62 Q122 48 134 58 Q138 64 128 68 Q114 70 100 66"
                    fill="none"
                    stroke="#B83610"
                    strokeWidth="1.2"
                    opacity="0.55"
                />
            </GiftLayer>

            {/* 7. Bow knot (center pinch — arrives last) */}
            <GiftLayer delay={0.95} drop={10}>
                <ellipse
                    cx="100"
                    cy="66"
                    rx="9"
                    ry="12"
                    fill="#C9390F"
                />
                <ellipse
                    cx="100"
                    cy="63"
                    rx="4"
                    ry="4"
                    fill="#F87C4A"
                    opacity="0.65"
                />
            </GiftLayer>

            {/* 8. Sparkles / stars — celebratory finish */}
            <motion.g
                initial={{ opacity: 0, scale: 0.4 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{
                    duration: 0.5,
                    delay: 1.15,
                    ease: [0.19, 1, 0.22, 1],
                }}
                style={{ transformOrigin: "100px 100px" }}
            >
                <g fill="#F2B438">
                    {/* 4-point star helper via two crossed diamonds */}
                    <g transform="translate(20 40)">
                        <path d="M0 -6 L2 -1 L6 0 L2 1 L0 6 L-2 1 L-6 0 L-2 -1 Z" />
                    </g>
                    <g transform="translate(178 30) scale(0.85)">
                        <path d="M0 -6 L2 -1 L6 0 L2 1 L0 6 L-2 1 L-6 0 L-2 -1 Z" />
                    </g>
                    <g transform="translate(10 108) scale(0.7)">
                        <path d="M0 -6 L2 -1 L6 0 L2 1 L0 6 L-2 1 L-6 0 L-2 -1 Z" />
                    </g>
                    <g transform="translate(190 118) scale(0.75)">
                        <path d="M0 -6 L2 -1 L6 0 L2 1 L0 6 L-2 1 L-6 0 L-2 -1 Z" />
                    </g>
                </g>
                {/* Small orange dots */}
                <g fill="#EB4A18" opacity="0.7">
                    <circle cx="40" cy="24" r="2" />
                    <circle cx="160" cy="52" r="1.8" />
                    <circle cx="30" cy="80" r="1.5" />
                    <circle cx="172" cy="92" r="2" />
                </g>
            </motion.g>
        </svg>
    );
}
